import styled, { css } from "styled-components";

const focusRing = css`
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.envy};
    outline-offset: 2px;
  }
`;

export const Row = styled.section`
  align-items: center;
  background: ${({ theme }) => theme.colors.navy300};
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha10};
  border-radius: 8px;
  box-sizing: border-box;
  display: flex;
  flex-wrap: wrap;
  gap: 12px 16px;
  padding: 14px 16px;
  width: 100%;
`;

export const Identity = styled.div`
  align-items: center;
  display: flex;
  flex: 1;
  gap: 12px;
  min-width: 180px;

  img {
    border-radius: 8px;
    flex-shrink: 0;
    height: 36px;
    width: 36px;
  }
`;

export const Name = styled.span`
  color: ${({ theme }) => theme.colors.ice};
  display: block;
  font-size: 16px;
  font-weight: 600;
  line-height: 20px;
`;

export type Tone = "alert" | "waiting" | "positive" | "muted";

const TONE_COLOR = {
  alert: "alert",
  waiting: "caution",
  positive: "envy",
  muted: "chalk",
} as const;

export const Status = styled.span<{ $tone: Tone }>`
  color: ${({ theme, $tone }) => theme.colors[TONE_COLOR[$tone]]};
  display: block;
  font-size: 13px;
  font-weight: 600;
  line-height: 18px;
`;

export const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const button = css`
  align-items: center;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  display: inline-flex;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  gap: 8px;
  justify-content: center;
  padding: 9px 14px;
  text-decoration: none;
  transition: background 0.15s ease;
  white-space: nowrap;

  svg {
    flex-shrink: 0;
    height: 16px;
    width: 16px;
  }

  ${focusRing}
`;

export const SecondaryButton = styled.a`
  ${button}
  background: ${({ theme }) => theme.colors.navy100};
  color: ${({ theme }) => theme.colors.ice};

  &:hover {
    background: ${({ theme }) => theme.colors.navy50};
    color: ${({ theme }) => theme.colors.ice};
  }
`;

const primary = css`
  ${button}
  background: ${({ theme }) => theme.colors.envy};
  color: ${({ theme }) => theme.colors.ice};

  &:hover {
    background: ${({ theme }) => theme.colors.darkerEnvy};
    color: ${({ theme }) => theme.colors.ice};
  }

  &[aria-disabled="true"] {
    background: ${({ theme }) => theme.colors.chalkAlpha10};
    color: ${({ theme }) => theme.colors.chalkAlpha40};
    cursor: default;
  }
`;

export const PrimaryLink = styled.a`
  ${primary}
`;

export const PrimaryButton = styled.button`
  ${primary}
`;

export const Footnote = styled.p`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 14px;
  line-height: 20px;
  margin: 10px 0 0;
`;

export const TextButton = styled.button`
  background: none;
  border: none;
  border-radius: 4px;
  color: ${({ theme }) => theme.colors.ice};
  cursor: pointer;
  font: inherit;
  font-weight: 600;
  padding: 0;
  text-decoration: underline;
  text-decoration-color: ${({ theme }) => theme.colors.chalkAlpha40};
  text-underline-offset: 3px;

  &:hover {
    text-decoration-color: ${({ theme }) => theme.colors.ice};
  }

  ${focusRing}
`;
